// Stage Mail-3:MailList。
//
// MailPage 左部 — inbound + outbound 一覧。query 経由で status / is_read /
// is_important を絞り込み、ページネーション。
//
// design §5.3:
// - mock.ts 直接 import はしない(メール実データは API から来る)
// - ページネーション (page / page_size)対応
// - 件名 / 送信者 / 日付 / unread / important / status を一覧表示

import React, { useCallback, useEffect, useState } from "react";
import { Star, Mail, MailOpen } from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { mailApi, type MailMessage, type MailStatus } from "../../api/mail";
import { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { MailStatusBadge } from "./MailStatusBadge";
import { formatJstDateTime } from "../../utils/dateTime";

const STATUS_FILTERS: { value: MailStatus | "all"; label: string }[] = [
  { value: "all", label: "全て" },
  { value: "unhandled", label: "未対応" },
  { value: "handling", label: "対応中" },
  { value: "completed", label: "対応済み" },
];

export interface MailListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** 親から再読込される度に refresh できる 値(未読数カウンタ 等) */
  refreshSignal?: number;
}

export function MailList({ selectedId, onSelect, refreshSignal = 0 }: MailListProps) {
  const { hasAnyRole } = useAuth();
  const canPatch = hasAnyRole(["system_admin", "editor"]);

  const [items, setItems] = useState<MailMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [status, setStatus] = useState<MailStatus | "all">("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q =
        status === "all"
          ? { page, page_size: pageSize }
          : { page, page_size: pageSize, status: status as MailStatus };
      const data = await mailApi.listMessages(q);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError(String(e));
      }
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  async function toggleRead(m: MailMessage) {
    if (!canPatch) return;
    try {
      await mailApi.patchMessage(m.id, { is_read: !m.is_read });
      load();
    } catch (e) {
      window.alert(`既読切替失敗: ${e instanceof ApiError ? e.message : String(e)}`);
    }
  }

  async function toggleImportant(m: MailMessage) {
    if (!canPatch) return;
    try {
      await mailApi.patchMessage(m.id, { is_important: !m.is_important });
      load();
    } catch (e) {
      window.alert(`重要切替失敗: ${e instanceof ApiError ? e.message : String(e)}`);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card noPadding>
      <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #e5e7eb" }}>
        <div className="tabs" style={{ marginBottom: 8 }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`tab ${status === f.value ? "active" : ""}`}
              onClick={() => {
                setStatus(f.value);
                setPage(1);
              }}
              style={{ padding: "0 10px", fontSize: 12 }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          {loading ? "読み込み中..." : `全 ${total} 件`}
        </div>
        {error && (
          <div
            style={{
              marginTop: 6,
              padding: "6px 10px",
              background: "#fef2f2",
              color: "#b91c1c",
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}
      </div>
      <div style={{ overflowY: "auto", flex: 1, maxHeight: "calc(100vh - 320px)" }}>
        {items.length === 0 && !loading ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            該当するメールはありません
          </div>
        ) : (
          items.map((m) => (
            <div
              key={m.id}
              className={`mail-list-item ${m.id === selectedId ? "active" : ""}`}
              style={{ borderRadius: 0, padding: "10px 16px" }}
              onClick={() => onSelect(m.id)}
            >
              <div className="mail-list-from">
                <span
                  className="mail-list-name"
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  {!m.is_read && (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#2563eb",
                      }}
                    />
                  )}
                  {m.from_name || m.from_email}
                </span>
                <span className="mail-list-date">
                  {m.received_at ? formatJstDateTime(m.received_at) : ""}
                </span>
              </div>
              <div className="mail-list-subject">
                {m.is_important && (
                  <Star
                    size={11}
                    fill="#f59e0b"
                    stroke="#f59e0b"
                    style={{ display: "inline", marginRight: 4 }}
                  />
                )}
                {m.subject || "(無題)"}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginTop: 6,
                  alignItems: "center",
                }}
              >
                <MailStatusBadge status={m.status} size="sm" />
                {m.direction === "outbound" && <Badge color="blue">送信</Badge>}
                <span
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  {canPatch && (
                    <>
                      <button
                        type="button"
                        aria-label={m.is_read ? "未読にする" : "既読にする"}
                        title={m.is_read ? "未読にする" : "既読にする"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRead(m);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: m.is_read ? "#9ca3af" : "#2563eb",
                          padding: 2,
                          cursor: "pointer",
                        }}
                      >
                        {m.is_read ? <Mail size={13} /> : <MailOpen size={13} />}
                      </button>
                      <button
                        type="button"
                        aria-label="重要切替"
                        title="重要切替"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleImportant(m);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: m.is_important ? "#f59e0b" : "#9ca3af",
                          padding: 2,
                          cursor: "pointer",
                        }}
                      >
                        <Star size={13} fill={m.is_important ? "#f59e0b" : "none"} />
                      </button>
                    </>
                  )}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      <div
        style={{
          padding: "8px 16px",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 12,
          color: "#6b7280",
        }}
      >
        <span>
          {total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} / {total}
        </span>
        <span className="row gap-6">
          <button
            className="btn-icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{ opacity: page <= 1 ? 0.4 : 1 }}
          >
            ‹ 前
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            className="btn-icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            style={{ opacity: page >= totalPages ? 0.4 : 1 }}
          >
            次 ›
          </button>
        </span>
      </div>
    </Card>
  );
}
